import { CONTENT_TYPES } from "@/utils/content";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRef } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode, Mousewheel, Keyboard } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

import ArticleCard from "../items/ArticleCard";
import SpotlightCard from "../items/SpotlightCard";
import VideoCard from "../items/VideoCard";
import NewsCard from "../items/NewsCard";
import GuideCard from "../items/GuideCard";
import { fitIn } from "@/utils/imageHandler";
import { useIsMobile } from "@/hooks/useIsMobile";

const CarouselTrending = ({ data, title }) => {
  const isMobile = useIsMobile();

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  // Show skeleton loading when data is empty
  if (!data || data.length === 0) {
    return (
      <div className='container'>
        <p className='text-xl font-medium leading-none text-white lg:text-3xl font-display'>
          {title}
        </p>
        <div className='flex gap-3 pt-4 overflow-hidden lg:gap-10 lg:pt-8'>
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className='h-[180px] lg:h-[250px] aspect-[93/125] bg-white/5 rounded-md animate-pulse flex-shrink-0'
            ></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='md:container'>
      <p className='px-4 text-xl font-medium leading-none text-white md:px-0 md:text-2xl lg:text-3xl font-display'>
        {title}
      </p>
      <div className='relative mt-4 lg:mt-8 group'>
        <Swiper
          modules={[Navigation, FreeMode, Mousewheel, Keyboard]}
          slidesPerView={"auto"}
          freeMode={true}
          mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
          keyboard={{ enabled: true }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          spaceBetween={12}
          breakpoints={{
            960: {
              spaceBetween: 16,
            },
          }}
          className='overflow-visible'
        >
          {data.map((item, index) => (
            <SwiperSlide
              key={index}
              className='!w-auto rounded-md overflow-hidden'
            >
              {item.content_type.name === CONTENT_TYPES.SPECIAL_REPORTS && (
                <Link
                  href={`reports/${item.slug}`}
                  className='relative block h-[180px] lg:h-[250px] aspect-[93/125]'
                >
                  <Image
                    src={fitIn(item.cover_image_url, 400)}
                    alt='Image'
                    layout='fill'
                    objectFit='cover'
                  />
                </Link>
              )}

              {item.content_type.name === CONTENT_TYPES.SPOTLIGHTS && (
                <SpotlightCard
                  cardHeight={"h-[180px] lg:h-[250px]"}
                  data={item}
                />
              )}

              {item.content_type.name === CONTENT_TYPES.ARTICLES && (
                <ArticleCard
                  cardHeight={"h-[180px] lg:h-[250px]"}
                  data={item}
                />
              )}

              {item.content_type.name === CONTENT_TYPES.VIDEOS && (
                <VideoCard videoHeight='h-[180px] lg:h-[250px]' data={item} />
              )}

              {item.content_type.name === CONTENT_TYPES.INFOBYTES && (
                <Link
                  href={`infobytes/${item.slug}`}
                  className='relative block h-[180px] lg:h-[250px] aspect-[384/288]'
                >
                  <Image
                    src={fitIn(item.thumbnail_url, 400)}
                    layout='fill'
                    objectFit='cover'
                    alt='Image'
                  />
                </Link>
              )}

              {item.content_type.name === CONTENT_TYPES.NEWS && (
                <NewsCard
                  cardHeight={"h-[180px] lg:h-[250px]"}
                  data={item}
                />
              )}

              {item.content_type.name === CONTENT_TYPES.PODCAST_EPISODES && (
                <VideoCard videoHeight='h-[180px] lg:h-[250px]' data={item} podcast={true} />
              )}

              {item.content_type.name === CONTENT_TYPES.GUIDES && (
                <div className='w-[220px] lg:w-[280px] h-[180px] lg:h-[250px] [&_.font-neon]:text-base [&_.font-neon]:lg:text-xl'>
                  <GuideCard data={item} size='w-full h-full' label='Founder Library' />
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation Buttons */}
        {!isMobile && (
          <>
            <div className='absolute top-0 left-0 z-10 flex items-center w-16 h-full transition duration-300 -translate-x-full opacity-0 group-hover:translate-x-0 group-hover:opacity-100 bg-gradient-to-r from-background via-background/70 to-transparent'>
              <button ref={prevRef}>
                <CaretLeft size={32} color='#fff' />
              </button>
            </div>

            <div className='absolute top-0 right-0 z-10 flex items-center justify-end w-16 h-full pr-4 transition duration-300 translate-x-full opacity-0 group-hover:translate-x-0 group-hover:opacity-100 bg-gradient-to-l from-background via-background/70 to-transparent'>
              <button ref={nextRef}>
                <CaretRight size={32} color='#fff' />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CarouselTrending;
